import React from "react";

/**
 * For debugging run-away prop re-renders, this hook can help you find which
 * props changed between renders.
 *
 * @example
 * function MyComponent({ to, from }) {
 *   const toDate = new Date(to);
 *   useLogChangedProps({
 *     toDate,  // Always logs toDate has changed, since `new Date(to)` is a new object each render!
 *     from     // Only logs if `from` changes from one render to the next
 *   });
 * }
 */
export function useLogChangedProps(props: Record<string, any>): void {
  // eslint-disable-next-line no-console
  console.groupCollapsed("Gathering changed props...");
  const lastProps = React.useRef<Map<string, any>>(new Map());
  for (const [propName, propValue] of Object.entries(props)) {
    if (!lastProps.current.has(propName)) {
      lastProps.current.set(propName, propValue);
    } else {
      const lastValue = lastProps.current.get(propName);
      if (lastValue !== propValue) {
        if (typeof lastValue === "function") {
          // eslint-disable-next-line no-console
          console.log(`${propName} changed from ${lastValue} to ${propValue}`);
        } else {
          // eslint-disable-next-line no-console
          console.log(
            `${propName} changed from ${JSON.stringify(
              lastValue
            )} to ${JSON.stringify(propValue)}`
          );
        }
        lastProps.current.set(propName, propValue);
      }
    }
  }
  // eslint-disable-next-line no-console
  console.groupEnd();
}
